// Lightweight, dependency-free JWT inspection used purely to detect a stale
// session on the client. This performs NO signature verification — that is
// impossible (and pointless) in the browser, where the secret is not present.
// The authoritative validation still happens server-side (operator-test-api
// rejects expired/forged tokens on every call). Here we only read the
// unverified `exp` claim so the SPA can proactively log the user out instead
// of leaving a dead session in place.

/**
 * Decodes the payload (second segment) of a JWT without verifying its
 * signature. Returns the parsed claims object, or `null` when the input is not
 * a structurally valid JWT (missing, wrong segment count, undecodable, or a
 * non-object payload).
 */
export function decodeJwtPayload(
  token: string | null | undefined,
): Record<string, unknown> | null {
  if (typeof token !== 'string' || token.length === 0) {
    return null;
  }

  const segments = token.split('.');
  if (segments.length !== 3) {
    return null;
  }

  try {
    const json = base64UrlDecode(segments[1]);
    const parsed = JSON.parse(json);
    if (parsed === null || typeof parsed !== 'object') {
      return null;
    }
    return parsed as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Returns `true` when the given JWT should be treated as expired and the user
 * logged out. A token counts as expired when it is missing, structurally
 * invalid, carries no numeric `exp` claim, or its `exp` instant is at/before
 * now. Treating missing/invalid tokens as expired is intentional: a logged-in
 * session with no usable token is a stale session.
 *
 * `exp` is interpreted as seconds since the Unix epoch per RFC 7519.
 */
export function isJwtExpired(token: string | null | undefined): boolean {
  const payload = decodeJwtPayload(token);
  if (payload === null) {
    return true;
  }

  const exp = payload.exp;
  if (typeof exp !== 'number' || !Number.isFinite(exp)) {
    return true;
  }

  return exp * 1000 <= Date.now();
}

function base64UrlDecode(segment: string): string {
  const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
  const paddingNeeded = (4 - (base64.length % 4)) % 4;
  const padded = base64.padEnd(base64.length + paddingNeeded, '=');

  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}
